/**
 * shared/pmove/footsteps.ts — PM_Footsteps (bobCycle + footstep events), surface types.
 *
 * OWNER: PMOVE agent.
 * T6 PM_Footsteps 0x9446c0 [PM §3 "Footsteps / view bob"]: BOBMOVE table @0xc8beac (same values as
 * T5 bobFactorTable), bobCycle += msec * bobmove * weapon cycle scale. The divisor and the speed
 * thresholds are hidden T6 dvars, so the T5 code (PM_GetMaxSpeed divisor, player_*Threshhold)
 * is used [T5 bg_pmove.cpp:PM_Footsteps, PM_GetMaxSpeed, PM_GetBobMove, BG_CalcBob,
 * PM_FootstepEvent, PM_FootstepType] — ESTIMATE where T6 differs.
 * The view-bob *amplitude* is client side (viewmodel/camera agent), not here.
 */
import { createTraceResult, SURF, surfaceTypeFromFlags } from '../collision/types';
import { ENTITYNUM_NONE, PM_BOBMOVE } from '../constants';
import type { Vec3 } from '../math';
import { PEv, PMF, PmType } from '../types';
import { cmdScaleForStance } from './stance';
import {
  effectiveStance, pmTrace, PM_FOOTSTEPS_THRESHOLD, PM_MOVE_THRESHOLD, PM_RUN_THRESHOLD,
  PM_RUNBK_THRESHOLD, PM_SPRINT_CAMERA_BOB, PM_SPRINT_THRESHOLD, PM_STANCE_STAND, STANCE_MASK_CLEAR,
  type Pm,
} from './pm';

/** Water surface type index (SURFACE_TYPES[20] = 'water'). */
const SURF_TYPE_WATER = 20;
/** Wood: ladder footsteps / jumps default (T5 uses 21). */
const SURF_TYPE_WOOD = 21;

/** PM_GroundSurfaceType [T5 bg_pmove.cpp:553]: NOSTEPS -> 0, in water -> water. */
export function groundSurfaceType(pm: Pm): number {
  if (pm.groundTrace.surfaceFlags & SURF.NOSTEPS) return 0;
  const t = surfaceTypeFromFlags(pm.groundTrace.surfaceFlags);
  if (pm.ps.waterlevel) return SURF_TYPE_WATER;
  return t;
}

/** PM_FootstepType [T5 bg_pmove.cpp:727] mapped onto our PEv footstep events. */
function footstepType(pm: Pm): PEv {
  const f = pm.ps.pm_flags;
  if (f & PMF.PRONE) return PEv.FOOTSTEP_PRONE; // 108
  if (f & PMF.DUCKED) return PEv.FOOTSTEP_CROUCH; // 106 run / 107 walk -> one crouch event
  if ((f & PMF.ADS_SLOW) || pm.ps.leanf !== 0) return PEv.FOOTSTEP_WALK; // 105
  if (f & PMF.SPRINTING) return PEv.FOOTSTEP_SPRINT; // 103
  return pm.xyspeed >= PM_RUN_THRESHOLD ? PEv.FOOTSTEP_RUN : PEv.FOOTSTEP_WALK; // 104 / 105
}

const ladMins: Vec3 = [0, 0, 0];
const ladMaxs: Vec3 = [0, 0, 0];
const ladEnd: Vec3 = [0, 0, 0];
const ladTr = createTraceResult();

/** PM_FootstepEvent [T5 bg_pmove.cpp:639]: one event each time bobCycle crosses a 64 boundary. */
export function footstepEvent(pm: Pm, oldCycle: number, newCycle: number, bFootStep: boolean): void {
  if (oldCycle >> 6 === newCycle >> 6) return;
  const ps = pm.ps;
  if (ps.groundEntityNum === ENTITYNUM_NONE) {
    if (!bFootStep || !(ps.pm_flags & PMF.LADDER)) return;
    // ladder: trace into the ladder for its surface
    ladMins[0] = pm.mins[0] + 6; ladMins[1] = pm.mins[1] + 6; ladMins[2] = 8;
    ladMaxs[0] = pm.maxs[0] - 6; ladMaxs[1] = pm.maxs[1] - 6; ladMaxs[2] = Math.max(pm.maxs[2], 8);
    ladEnd[0] = ps.origin[0] - 31 * ps.vLadderVec[0];
    ladEnd[1] = ps.origin[1] - 31 * ps.vLadderVec[1];
    ladEnd[2] = ps.origin[2] - 31 * ps.vLadderVec[2];
    pmTrace(pm, ladTr, ps.origin, ladMins, ladMaxs, ladEnd, pm.tracemask & STANCE_MASK_CLEAR & ~0x10000);
    let surf = surfaceTypeFromFlags(ladTr.surfaceFlags);
    if (ladTr.fraction === 1 || !surf) surf = SURF_TYPE_WOOD;
    pm.ctx.addEvent(ps, PEv.FOOTSTEP_WALK, surf); // T5 event 0x68 (ladder step)
    return;
  }
  if (bFootStep) pm.ctx.addEvent(ps, footstepType(pm), groundSurfaceType(pm));
}

/** Weapon cycle scale raw fields (weapon file), default 1. */
function weaponRawScale(pm: Pm, key: string): number {
  const v = pm.ctx.weaponDef(pm.ps.weapon).raw[key];
  return typeof v === 'number' ? v : 1;
}

/**
 * BG_CalcBob [T5 bg_pmove.cpp:4941]: (u8)(int)(old + msec*bobmove*weaponScale). The weapon scale
 * is the weapon file's sprint / ducked-sprint / dtp cycle scale [PM §3 "weaponScale"].
 */
function calcBob(pm: Pm, old: number, bobmove: number): number {
  const ps = pm.ps;
  let v = pm.msec * bobmove;
  if (ps.pm_flags & PMF.DIVE) v *= weaponRawScale(pm, 'dtpScale');
  else if ((ps.pm_flags & PMF.SPRINTING) && ps.viewHeightTarget === 40) v *= weaponRawScale(pm, 'duckedSprintScale');
  else if ((ps.pm_flags & PMF.SPRINTING) || ps.waterlevel >= 3) v *= weaponRawScale(pm, 'sprintScale');
  return Math.trunc(old + v) & 0xff;
}

/** PM_GetMaxSpeed [T5 bg_pmove.cpp:4840]: the speed the bob rate is normalised by. ESTIMATE for T6. */
function getMaxSpeed(pm: Pm, walking: boolean, sprinting: boolean): number {
  const ps = pm.ps;
  const d = pm.ctx.dvars;
  let s = ps.speed;
  if (pm.cmd.forwardmove) {
    if (pm.cmd.rightmove) {
      s *= ((d.strafeSpeedScale - 1) * 0.75 + 1 + 1) * 0.5;
      if (pm.cmd.forwardmove < 0) s *= (d.backSpeedScale + 1) * 0.5;
    } else if (pm.cmd.forwardmove < 0) {
      s *= d.backSpeedScale;
    }
  } else if (pm.cmd.rightmove) {
    s *= (d.strafeSpeedScale - 1) * 0.75 + 1;
  }
  if (walking) s *= 0.4;
  else if (sprinting) s *= 1.5;
  s *= pm.ctx.weapons.moveSpeedScale(ps);
  s *= cmdScaleForStance(pm);
  return s * ps.moveSpeedScaleMultiplier;
}

/** PM_Footstep_LadderMove [T5 bg_pmove.cpp:4811]. */
function footstepLadderMove(pm: Pm): boolean {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.LADDER)) return false;
  if (pm.cmd.serverTime - ps.jumpTime < 300) return true;
  const fLadderSpeed = ps.velocity[2];
  const fMaxSpeed = 0.5 * 1.5 * 127;
  const bobmove = (ps.pm_flags & PMF.ADS_SLOW) === 0 && ps.leanf === 0
    ? (fLadderSpeed / fMaxSpeed) * 0.45
    : (fLadderSpeed / (fMaxSpeed * 0.4)) * 0.35;
  const old = ps.bobCycle;
  ps.bobCycle = Math.trunc(old + pm.msec * bobmove) & 0xff;
  footstepEvent(pm, old, ps.bobCycle, true);
  return true;
}

/** PM_Footsteps [T5 bg_pmove.cpp:4660; T6 0x9446c0]. */
export function footsteps(pm: Pm): void {
  const ps = pm.ps;
  if (ps.pm_type >= PmType.FREEZE) return;
  pm.xyspeed = Math.sqrt(ps.velocity[0] * ps.velocity[0] + ps.velocity[1] * ps.velocity[1]);
  if (ps.pm_flags & PMF.DIVE) ps.bobCycle = calcBob(pm, ps.bobCycle, PM_SPRINT_CAMERA_BOB); // dtpBobMove ESTIMATE
  const iStance = effectiveStance(ps);
  const stanceFrontBack = iStance + (ps.pm_flags & PMF.BACKWARDS_RUN ? 3 : 0);
  const inAir = ps.groundEntityNum === ENTITYNUM_NONE && ps.pm_type !== PmType.NORMAL_LINKED && ps.pm_type !== PmType.LASTSTAND;
  if (inAir && !ps.waterlevel && !(ps.pm_flags & (PMF.DIVE | PMF.MANTLE))) {
    footstepLadderMove(pm);
    return;
  }
  let walking = (ps.pm_flags & PMF.ADS_SLOW) !== 0 || ps.leanf !== 0;
  let sprinting = (ps.pm_flags & PMF.SPRINTING) !== 0;
  if ((PM_MOVE_THRESHOLD > pm.xyspeed || ps.pm_type === PmType.NORMAL_LINKED) && !(ps.pm_flags & PMF.MANTLE)) return; // NotMoving
  if (!pm.cmd.forwardmove && !pm.cmd.rightmove) return; // NotTryingToMove
  if (iStance === PM_STANCE_STAND && !(ps.pm_flags & PMF.DIVE)) {
    walking ||= PM_RUNBK_THRESHOLD >= pm.xyspeed;
    sprinting &&= pm.xyspeed >= PM_SPRINT_THRESHOLD;
  }
  const fMaxSpeed = getMaxSpeed(pm, walking, sprinting);
  // PM_GetBobMove: BOBMOVE[stance][walking] or player_sprintCameraBob when sprinting standing
  const bobFactor = stanceFrontBack || !sprinting ? PM_BOBMOVE[stanceFrontBack][walking ? 1 : 0] : PM_SPRINT_CAMERA_BOB;
  let bobmove = fMaxSpeed > 0 ? (pm.xyspeed / fMaxSpeed) * bobFactor : 0;
  if (ps.pm_flags & (PMF.DIVE | PMF.MANTLE)) return;
  const old = ps.bobCycle;
  if (ps.waterlevel >= 3) bobmove = 0.6; // bg_weaponBobFrequencySwimming ESTIMATE (unused on Hijacked)
  ps.bobCycle = calcBob(pm, old, bobmove);
  footstepEvent(pm, old, ps.bobCycle, pm.xyspeed >= PM_FOOTSTEPS_THRESHOLD);
}
